// Angular Files
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';

// Other External Files
import { firstValueFrom, forkJoin } from 'rxjs';

// Teller Online Files
import { AuthApiClient } from 'apps/admin-portal/src/app/core/api/AdminPortalApiClients';
import { TellerOnlineWindowService, TellerOnlineMessageService } from 'teller-online-libraries/shared';
import { AuthService } from './auth.service';
import { ConfigurationService } from './configuration.service';

// Teller Online Library Files

@Injectable({
    providedIn: 'root'
})
export class AuthGuardService {
    // Cache for all-systems permission check result to avoid repeated API calls
    private _hasAllSystemsPermission: boolean | null = null;

    constructor(
        private router: Router,
        private authService: AuthService,
        private authApiClient: AuthApiClient,
        private configurationService: ConfigurationService,
        private windowService: TellerOnlineWindowService,
        private messageService: TellerOnlineMessageService
    ) { }

    /**
     * Returns the cached all-systems permission status, or null if not yet checked.
     */
    public get hasAllSystemsPermission(): boolean | null {
        return this._hasAllSystemsPermission;
    }

    /**
     * Clears the cached permission check result.
     * Call this when the user logs out or when systems configuration changes.
     */
    public clearAllSystemsPermissionCache(): void {
        this._hasAllSystemsPermission = null;
    }

    canActivateChild(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
        return this.canActivate(route);
    }

    async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
        this.authService.setLastActiveTime();

        const isLoggedIn = this.authService.isAuthenticated;
        const isLoginForm = route.routeConfig.path === 'sign-in';
        const isHoldPage = route.routeConfig.path === 'hold-page';
        const isConfigPage = route.routeConfig.path === 'config';

        // In-app navigations. The initial page load is captured in the APP_INITIALIZER.
        // Set before the AD config fetch below so it still lands if that fetch fails.
        this.authService.setTellerLogon(route.queryParams.tellerLogon);

        // A failed fetch degrades to /sign-in below rather than leaving the guard rejected.
        // getAdConfig() itself logs the failure (once per outage), so nothing to do here.
        const adConfig = await this.authService.getAdConfig().catch(() => null);

        if (adConfig?.isAdEnabled && !isLoggedIn && !isHoldPage && !this.authService.tellerLogon) {
            this.router.navigate(['/hold-page'], {
                state: {
                    redirectToRoute: route.routeConfig.path
                }
            });
            return false;
        }

        // Do not check isHoldPage as the hold page handles redirection internally if user is already logged in
        if (isLoggedIn && isLoginForm) {
            this.router.navigate(['/']);
            return false;
        }

        const adDisabledOrBypassed = !adConfig?.isAdEnabled || this.authService.tellerLogon;
        if (!isLoggedIn && !isLoginForm && adDisabledOrBypassed) {
            this.router.navigate(['/sign-in'], {
                state: {
                    redirectToRoute: route.routeConfig.path
                }
            });
        }

        const isAuthenticated = isLoggedIn || isLoginForm || isHoldPage;

        // Check for required privilege if specified in route data
        if (!isConfigPage && isAuthenticated && route.data?.requiredPrivilege) {
            const hasPrivilege = await this.checkRequiredPrivilege(route.data.requiredPrivilege);
            if (!hasPrivilege) {
                this.messageService.notification(
                    'You do not have permission to access this page.',
                    'error',
                    5000
                );
                return this.router.createUrlTree(['/dashboard']);
            }
        }

        // Check for required privilege in ALL configured systems if specified in route data
        if (!isConfigPage && isAuthenticated && route.data?.requiredPrivilegeAllSystems) {
            const hasPrivilege = await this.checkRequiredPrivilegeInAllSystems(route.data.requiredPrivilegeAllSystems);
            if (!hasPrivilege) {
                // Only show the error if the user navigated directly to this page
                // (not via a redirectTo from /config)
                const initialUrl = this.router.currentNavigation()?.initialUrl?.toString();
                const wasRedirectedFromConfig = initialUrl === '/config' || initialUrl === '/config/';
                if (!wasRedirectedFromConfig) {
                    this.messageService.notification(
                        'You do not have permission to access this page. This feature requires the privilege in all configured systems.',
                        'error',
                        5000
                    );
                }
                // Redirect to teller-connections page which doesn't require the all-systems check
                return this.router.createUrlTree(['/config/teller-connections']);
            }
        }

        return isAuthenticated;
    }

    /**
     * Checks if the user has the specified privilege in any of their systems.
     */
    private async checkRequiredPrivilege(requiredPrivilege: string): Promise<boolean> {
        try {
            const userInfo = await firstValueFrom(this.authApiClient.getTellerUserInfo());

            if (userInfo?.isSuperUser) {
                return true;
            }

            if (!userInfo?.systems || !Array.isArray(userInfo.systems)) {
                return false;
            }

            return userInfo.systems.some((system: any) => {
                if (!system.privileges || !Array.isArray(system.privileges)) {
                    return false;
                }
                return system.privileges.includes(requiredPrivilege);
            });
        } catch {
            this.messageService.notification(
                'Unable to verify permissions. Please try again.',
                'error',
                5000
            );
            return false;
        }
    }

    /**
     * Checks if the user has the specified privilege in ALL configured teller systems.
     * Returns true only if the user has the privilege in every single configured system.
     */
    public async checkRequiredPrivilegeInAllSystems(requiredPrivilege: string): Promise<boolean> {
        // Return cached result if available
        if (this._hasAllSystemsPermission !== null) {
            return this._hasAllSystemsPermission;
        }

        try {
            const result = await firstValueFrom(forkJoin({
                configuredSystems: this.configurationService.getTellerConnections(),
                userInfo: this.authApiClient.getTellerUserInfo()
            }));

            if (result.userInfo?.isSuperUser) {
                this._hasAllSystemsPermission = true;
                return true;
            }

            const configuredSystems = result.configuredSystems || [];
            const userSystems = result.userInfo?.systems || [];

            // If no systems are configured, allow access (nothing to check against)
            if (configuredSystems.length === 0) {
                this._hasAllSystemsPermission = true;
                return true;
            }

            // Check if user has the privilege in ALL configured systems
            const hasPrivilegeInAllSystems = configuredSystems.every(configuredSystem => {
                // Find the user's system info that matches this configured system
                const userSystem = userSystems.find(us => us.systemId === configuredSystem.tellerId);

                // If user doesn't have access to this system at all, they don't have the privilege
                if (!userSystem) {
                    return false;
                }

                // Check if the user has the required privilege in this system
                return userSystem.privileges?.includes(requiredPrivilege) ?? false;
            });

            this._hasAllSystemsPermission = hasPrivilegeInAllSystems;
            return hasPrivilegeInAllSystems;
        } catch {
            this.messageService.notification(
                'Unable to verify permissions. Please try again.',
                'error',
                5000
            );
            this._hasAllSystemsPermission = false;
            return false;
        }
    }
}
